/**
 * Invoice List Component - Trade Loop v2.0
 *
 * Displays user's billing history and invoices
 * - List of past invoices
 * - Invoice status indicators
 * - Download PDF functionality
 * - Pagination support
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface Invoice {
  id: string;
  userId: string;
  subscriptionId: string;
  stripeInvoiceId: string | null;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: string;
  invoicePdfUrl: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  dueDate: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceListProps {
  limit?: number;
  showPagination?: boolean;
}

export function InvoiceList({ limit = 10, showPagination = true }: InvoiceListProps) {
  const { showError, showInfo } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage]);

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);

      // Use api utility (cookies sent automatically)
      const data = await api.get('/api/workflows/billing/invoices', {
        params: {
          page: currentPage,
          limit: limit,
        },
      });

      if (data.success) {
        setInvoices(data.data.invoices);
        setHasMore(data.data.pagination.hasMore);
        setTotalPages(Math.ceil(data.data.pagination.total / limit));
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      showError('Failed to Load Invoices', 'Unable to retrieve your billing history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async (invoiceId: string) => {
    try {
      showInfo('Preparing download...', 'Please wait');

      // Fetch PDF through backend proxy endpoint
      const response = await fetch(`/api/workflows/billing/invoices/${invoiceId}/download`, {
        method: 'GET',
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to download PDF');
      }

      // Create blob from response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error('Failed to download PDF:', error);

      // Show specific error message
      const errorMessage = error?.message || 'Unable to download invoice PDF';
      showError('Download Failed', errorMessage);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Paid
          </span>
        );
      case 'open':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
            <Clock className="w-3 h-3" />
            Open
          </span>
        );
      case 'void':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Void
          </span>
        );
      case 'uncollectible':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Uncollectible
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
            {status}
          </span>
        );
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isLoading && invoices.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-pulse">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Loading invoices...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (invoices.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Invoices Yet</h3>
          <p className="text-gray-600">Your billing history will appear here once you subscribe to a paid plan.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-600" />
          Billing History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Invoice Date */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Invoice Date</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(invoice.createdAt)}</p>
                </div>

                {/* Billing Period */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Billing Period</p>
                  <p className="text-sm text-gray-700">
                    {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                  </p>
                </div>

                {/* Amount */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Amount</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(invoice.amountPaid || invoice.amountDue, invoice.currency)}
                  </p>
                </div>

                {/* Status */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  {getStatusBadge(invoice.status)}
                </div>
              </div>

              {/* Actions */}
              <div className="ml-4 flex items-center gap-2">
                {/* Show download button if invoice is paid or has Stripe ID */}
                {(invoice.status === 'paid' || invoice.stripeInvoiceId) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPDF(invoice.id)}
                    className="flex items-center gap-2"
                    disabled={invoice.status === 'draft'}
                    title={
                      invoice.status === 'draft'
                        ? 'PDF will be available once invoice is finalized'
                        : 'Download invoice PDF'
                    }
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </Button>
                )}
                {/* Show status message if no PDF available */}
                {!invoice.stripeInvoiceId && invoice.status !== 'paid' && (
                  <span className="text-xs text-gray-500 italic">Processing...</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {showPagination && totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={!hasMore || isLoading}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
